import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtDate, fmtMoney, fmtQty, downloadReport, errMsg } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { PageHeader } from "../components/common";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, ClipboardEdit } from "lucide-react";

const statusStyle = (s) => ({
  created: "text-slate-700 bg-slate-100",
  released: "text-blue-700 bg-blue-100",
  in_progress: "text-amber-700 bg-amber-100",
  tech_completed: "text-indigo-700 bg-indigo-100",
  closed: "text-emerald-700 bg-emerald-100",
  cancelled: "text-red-700 bg-red-100",
  open: "text-amber-700 bg-amber-100",
  reviewed: "text-blue-700 bg-blue-100",
  converted: "text-emerald-700 bg-emerald-100",
}[s] || "text-slate-600 bg-slate-100");

export default function MntHistory() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [eid, setEid] = useState("");
  const [df, setDf] = useState("");
  const [dt, setDt] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/equipment", { params: { active: true } }).then((r) => setEquipment(r.data)); }, []);
  useEffect(() => {
    if (!eid) { setData(null); return; }
    const params = {};
    if (df) params.date_from = df;
    if (dt) params.date_to = dt;
    api.get(`/mnt/equipment/${eid}/history`, { params }).then((r) => setData(r.data));
  }, [eid, df, dt]);

  const exportFile = async (fmt) => {
    if (!eid) return;
    try {
      const parts = [];
      if (df) parts.push(`date_from=${df}`);
      if (dt) parts.push(`date_to=${dt}`);
      parts.push(`format=${fmt}`);
      await downloadReport(`/reports/mnt/equipment-history/${eid}?${parts.join("&")}`,
        `historial_${data?.equipment?.code || eid}.${fmt === "xlsx" ? "xlsx" : fmt}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="mnt-history-page">
      <PageHeader title={t("mnt.h.title")} subtitle={t("mnt.h.subtitle")}>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-[9px] font-bold uppercase tracking-widest">{t("mnt.equipment")}</Label>
            <Select value={eid || "none"} onValueChange={(v) => setEid(v === "none" ? "" : v)}>
              <SelectTrigger className="rounded-none h-8 text-xs w-72" data-testid="history-equipment-select"><SelectValue placeholder={t("mnt.selectEquipment")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}{e.model ? ` · ${e.model}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[9px] font-bold uppercase tracking-widest">{t("mnt.h.dateFrom")}</Label>
            <Input type="date" value={df} onChange={(e) => setDf(e.target.value)} className="rounded-none h-8 text-xs w-36" data-testid="history-date-from" />
          </div>
          <div>
            <Label className="text-[9px] font-bold uppercase tracking-widest">{t("mnt.h.dateTo")}</Label>
            <Input type="date" value={dt} onChange={(e) => setDt(e.target.value)} className="rounded-none h-8 text-xs w-36" data-testid="history-date-to" />
          </div>
          {eid && (
            <>
              <div className="flex border">
                <button onClick={() => exportFile("csv")} className="border-r px-2 h-8 text-[10px] font-bold hover:bg-muted flex items-center gap-1" data-testid="history-export-csv"><FileText size={12} /> CSV</button>
                <button onClick={() => exportFile("xlsx")} className="px-2 h-8 text-[10px] font-bold hover:bg-muted flex items-center gap-1" data-testid="history-export-xlsx"><FileSpreadsheet size={12} /> XLSX</button>
              </div>
              <button onClick={() => navigate(`/mnt/equipment/${eid}/internal-order`)} className="border px-2 h-8 text-[10px] font-bold hover:bg-muted flex items-center gap-1" data-testid="history-view-io">
                <ClipboardEdit size={12} /> {t("eq.viewInternalOrder")}
              </button>
            </>
          )}
        </div>
      </PageHeader>

      {!data ? <div className="text-xs text-muted-foreground border border-dashed py-10 text-center">{t("mnt.h.selectEq")}</div> : (
        <div className="space-y-6">
          <div className="border bg-card p-4">
            <div className="text-lg font-black uppercase tabular-nums">{data.equipment.code}</div>
            <div className="text-sm">{data.equipment.name}{data.equipment.model ? <span className="text-muted-foreground"> · {data.equipment.model}</span> : null}</div>
            <div className="text-[11px] text-muted-foreground mt-1">{data.equipment.area} {data.equipment.location && `· ${data.equipment.location}`}</div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest mb-2">{t("nav.mnt.workOrders")} ({data.work_orders.length})</div>
            {data.work_orders.length === 0 ? <div className="text-[11px] text-muted-foreground py-4 text-center border border-dashed">—</div> : (
              <div className="tbl-wrap"><table className="tbl">
                <thead><tr><th>{t("mnt.wo.number")}</th><th>{t("mnt.wo.type")}</th><th>{t("mnt.description")}</th><th>{t("mnt.wo.technician")}</th><th>{t("common.status")}</th><th>{t("mnt.wo.downtime")}</th><th>{t("mnt.wo.totalCost")}</th><th>{t("mnt.wo.actualEnd")}</th></tr></thead>
                <tbody>{data.work_orders.map((w) => (
                  <tr key={w.id} onClick={() => navigate(`/mnt/work-orders/${w.id}`)} className="cursor-pointer hover:bg-muted/50" data-testid={`hist-wo-${w.wo_number}`}>
                    <td className="font-bold tabular-nums">{w.wo_number}</td>
                    <td className="text-[10px] font-bold">{w.type}</td>
                    <td className="text-[11px] max-w-64 truncate">{w.description}</td>
                    <td className="text-[11px] font-bold">{w.technician_name || w.assigned_to || "—"}</td>
                    <td><span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase ${statusStyle(w.status)}`}>{t(`mnt.wo.status.${w.status}`)}</span></td>
                    <td className="tabular-nums">{fmtQty(w.downtime_hours)} h</td>
                    <td className="tabular-nums font-bold">{fmtMoney(w.total_cost || 0)}</td>
                    <td className="text-[10px] text-muted-foreground tabular-nums">{w.actual_end ? fmtDate(w.actual_end) : "—"}</td>
                  </tr>))}
                </tbody>
              </table></div>
            )}
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest mb-2">{t("nav.mnt.notifications")} ({data.notifications.length})</div>
            {data.notifications.length === 0 ? <div className="text-[11px] text-muted-foreground py-4 text-center border border-dashed">—</div> : (
              <div className="tbl-wrap"><table className="tbl">
                <thead><tr><th>{t("mnt.n.number")}</th><th>{t("mnt.n.type")}</th><th>{t("mnt.priority")}</th><th>{t("mnt.n.affectedSystem")}</th><th>{t("mnt.description")}</th><th>{t("mnt.reportedBy")}</th><th>{t("mnt.reportedAt")}</th><th>{t("common.status")}</th></tr></thead>
                <tbody>{data.notifications.map((n) => (
                  <tr key={n.id} data-testid={`hist-notif-${n.notif_number}`}>
                    <td className="font-bold tabular-nums">{n.notif_number}</td>
                    <td className="text-[10px] font-bold">{n.type}</td>
                    <td className="text-[10px]">{t(`mnt.p.${n.priority}`)}</td>
                    <td className="text-[10px]">{n.affected_system ? t(`mnt.as.${n.affected_system}`) : "—"}</td>
                    <td className="text-[11px] max-w-72 truncate">{n.description}</td>
                    <td className="text-[10px] text-muted-foreground">{n.reported_by}</td>
                    <td className="text-[10px] text-muted-foreground tabular-nums">{fmtDate(n.reported_at)}</td>
                    <td><span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase ${statusStyle(n.status)}`}>{t(`mnt.n.status.${n.status}`)}</span></td>
                  </tr>))}
                </tbody>
              </table></div>
            )}
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest mb-2">{t("nav.mnt.meters")} ({data.meters.length})</div>
            {data.meters.length === 0 ? <div className="text-[11px] text-muted-foreground py-4 text-center border border-dashed">—</div> : (
              <div className="tbl-wrap"><table className="tbl">
                <thead><tr><th>{t("mnt.m.code")}</th><th>{t("mnt.m.type")}</th><th>{t("mnt.m.lastReading")}</th><th>{t("mnt.m.uom")}</th><th>{t("mnt.m.lastReadingAt")}</th></tr></thead>
                <tbody>{data.meters.map((m) => (
                  <tr key={m.id} data-testid={`hist-meter-${m.code}`}>
                    <td className="font-bold tabular-nums">{m.code}</td>
                    <td className="text-[10px] font-bold">{t(`mnt.m.type.${m.meter_type}`)}</td>
                    <td className="tabular-nums font-bold">{fmtQty(m.last_reading)}</td>
                    <td className="text-[11px]">{m.uom}</td>
                    <td className="text-[10px] text-muted-foreground">{m.last_reading_at ? fmtDate(m.last_reading_at) : "—"}</td>
                  </tr>))}
                </tbody>
              </table></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
